
import { defineComponent, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import BottomPopUpItem from '@/components/BottomPopUp/BottomPopUpItem.vue';
import AppBottomPopUp from '@/components/BottomPopUp/AppBottomPopUp.vue';
import { CHOOSER_TYPE, MEDIA_TYPES, UPLOAD_PLACES } from '@/types/enums';
import { useMainStore } from '@/store/mainStore';
import { useFlutterBus } from '@/store/flutterBus';
import {
  ICaseFilesManage,
  IFileUpload, IImageConverted, IImageCrop, ISearchLabelInput,
} from '@/types/interfaces';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import DriveAddPhotos from '@/modules/drive/components/DriveAddPhotos.vue';
import DriveDesktopAddPhotos from '@/modules/drive/components/desktop/cases/DriveDesktopAddPhotos.vue';
import DriveAddMenuPopUp from '@/modules/drive/components/UI/DriveAddMenuPopUp.vue';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import { bmpConvertor, convertBmpInFileList } from '@/helpers/bmpConvertor';
import useFlutter from '@/composables/UseFlutter';

export default defineComponent({
  props: {
    customControl: { type: Boolean, default: false },
    photosPopUpPos: { type: Object },
  },
  emits: ['uploadPhotos', 'addPhotos', 'close'],
  components: {
    BottomPopUpItem,
    AppBottomPopUp,
    DriveAddPhotos,
    DriveAddMenuPopUp,
    AppTakePhoto,
    DriveDesktopAddPhotos,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();

    const fileInput = ref<HTMLInputElement|null>(null);

    const mediaToCrop = ref({} as IImageCrop);
    const mediaInputRef = ref();
    const mediaLoadedKey = ref(0);
    const appTakePhoto = ref();
    const isAddPhotosPopUp = ref(false);
    const currentChooserType = ref('');

    const popUpPosition = ref(props.photosPopUpPos);

    const { onUploadUpdate, uploadInfo } = UseUpload();
    const {
      flutterOpenCamera,
      flutterOpenImagePicker,
      flutterOpenFilePicker,
      flutterAddMedia,
    } = useFlutter();

    function setChooserType(chooserType) {
      currentChooserType.value = chooserType;
      const { nativeApp } = window as any;

      if (nativeApp && chooserType) {
        nativeApp.setChooserType(chooserType);
      }
    }

    async function addMedia(e: Event) {
      if (props.customControl) {
        const fileList: FileList | null = (e.target as HTMLInputElement).files;
        const newFileList = await convertBmpInFileList(fileList as unknown as FileList);
        emit('uploadPhotos', newFileList);
        return;
      }
      let isBmp;
      if (e.target) {
        isBmp = (e.target as HTMLInputElement).files ? (e.target as HTMLInputElement).files![0].type.includes('bmp') : false;
      }
      const img: File = (e.target as HTMLInputElement).files![0];

      let converted = {} as IImageConverted;

      if (isBmp) {
        converted = await bmpConvertor((e.target as HTMLInputElement).files![0]);
      }

      mediaToCrop.value = {
        url: isBmp ? converted.url : URL.createObjectURL(img),
        fileName: isBmp ? converted.name : img.name,
        fileType: isBmp ? converted.type : img.type,
      };
      mediaLoadedKey.value += 1;

      mediaInputRef.value!.value = '';
    }

    watch(() => flutterBus.flutterCameraFile, (newValue) => {
      if (newValue) {
        flutterAddMedia(newValue).then((r) => {
          mediaToCrop.value = r;
        });
        flutterBus.flutterCameraFile = null;
      }
    });

    function hidePopUp() {
      if (props.customControl || !mainStore.isMobile) {
        emit('close');
        return;
      }
      mainStore.BottomPopUpType = null;
    }

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
      appTakePhoto.value.resetAppTakePhoto();
    }

    const labels = ref([] as ISearchLabelInput[]);

    function handleLabels() {
      return mainStore.onSearchInputLabels('').then((response) => {
        labels.value = response;
        return response;
      });
    }

    function handleMultipleUpload(f: any) {
      const fileUploads = f.file as IFileUpload[];
      onUploadUpdate().setStartInfo(fileUploads);

      const caseId = driveStore.currentCase.id || undefined;
      const folderId = driveStore.currentCase.folderId || undefined;

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId, mainStore.recentInputLabels, true))
        .then((files) => {
          if (caseId) {
            const filesPayload = {
              id: caseId,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          }
        });
      hidePopUp();
    }

    function flutterUploadFiles(f: File[]) {
      // const fileUploads = f as IFileUpload[];
      if (props.customControl) {
        emit('uploadPhotos', f);
        return;
      }
      const fileUploads = UploadService.toFileUploads(f);
      onUploadUpdate().setStartInfo(fileUploads);

      const caseId = driveStore.currentCase.id || undefined;
      const folderId = driveStore.currentCase.folderId || undefined;

      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId, mainStore.recentInputLabels, true))
        .then((files) => {
          if (caseId) {
            const filesPayload = {
              id: caseId,
              fileIds: files.map((file) => file.id),
            } as ICaseFilesManage;

            driveStore.addFilesToCase(filesPayload);
          }
        });
      hidePopUp();
    }

    watch(() => flutterBus.flutterImagePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        flutterUploadFiles(nv);
        flutterBus.flutterImagePickerFiles = null;
      }
    });

    function handleRetake() {
      setChooserType(currentChooserType.value);
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    function closeAddPhotos() {
      isAddPhotosPopUp.value = false;
      hidePopUp();
    }

    function openAddPhotos() {
      if (props.customControl) {
        emit('addPhotos');
        return;
      }
      isAddPhotosPopUp.value = true;
    }

    async function uploadPhotos(event: Event) {
      const fileList: FileList | null = (event.target as HTMLInputElement).files;

      if (props.customControl) {
        if (fileList) {
          const newFileList = await convertBmpInFileList(fileList);
          emit('uploadPhotos', newFileList);
        }
        return;
      }

      const caseId = driveStore.currentCase.id || undefined;

      if (fileList) {
        const newFileList = await convertBmpInFileList(fileList);
        const fileUploads = UploadService.toFileUploads(newFileList);
        const folderId = driveStore.currentCase.folderId || undefined;

        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.CASE_PHOTO, id: caseId }, folderId)
          .then((files) => {
            if (caseId) {
              const filesPayload = {
                id: caseId,
                fileIds: files.map((file) => file.id),
              } as ICaseFilesManage;

              driveStore.addFilesToCase(filesPayload).then((r) => {
                if (driveStore.currentCase.id === filesPayload.id) {
                  const caseIdx = driveStore.cases.data.findIndex((el) => el.id === filesPayload.id);
                  const matrix = [] as any[];
                  for (let i = 0; i < filesPayload.fileIds.length; i += 1) {
                    matrix.push({ id: filesPayload.fileIds[i], toId: r.results.data[i].id, toName: r.results.data[i].name });
                  }
                  driveStore.cases.data[caseIdx].photos.forEach((el, index) => {
                    if (filesPayload.fileIds.includes(el.id)) {
                      const idx = matrix.findIndex((m) => m.id === el.id);
                      driveStore.cases.data[caseIdx].photos[index].id = matrix[idx].toId;
                    }
                  });
                }
              });
            }
          });
        hidePopUp();
      }

      fileInput.value!.value = '';
    }

    function cancelUpload() {
      hidePopUp();
      UploadService.isAbortUpload = true;
      handleMediaCancel();
    }

    return {
      fileInput,
      router,
      uploadPhotos,
      hidePopUp,
      closeAddPhotos,
      openAddPhotos,
      MEDIA_TYPES,

      isAddPhotosPopUp,
      mediaInputRef,
      mediaLoadedKey,
      mediaToCrop,
      appTakePhoto,
      labels,
      uploadInfo,
      addMedia,
      handleMediaCancel,
      handleRetake,
      handleMultipleUpload,
      cancelUpload,
      mainStore,
      flutterBus,
      setChooserType,
      CHOOSER_TYPE,
      popUpPosition,
      flutterOpenCamera,
      flutterOpenImagePicker,
      flutterOpenFilePicker,
    };
  },
});
